/**
 * Cable Service - API Implementation
 * Servicio para gestión de cables de fibra óptica via API REST
 */

import { apiClient } from './apiClient'
import type { Cable } from '@/types/fibers'
import { cableOrigenId, cableDestinoId } from '@/utils/cableEndpoints'

export const cableServiceApi = {
  /**
   * Obtener todos los cables
   */
  async getAll(): Promise<Cable[]> {
    return await apiClient.get<Cable[]>('/api/cables')
  },

  /**
   * Obtener un cable por ID
   */
  async getById(id: string): Promise<Cable> {
    return await apiClient.get<Cable>(`/api/cables/${id}`)
  },

  /**
   * Crear un nuevo cable
   */
  async create(cableData: Partial<Cable>): Promise<Cable> {
    return await apiClient.post<Cable>('/api/cables', cableData)
  },

  /**
   * Actualizar un cable existente
   */
  async update(id: string, cableData: Partial<Cable>): Promise<Cable> {
    return await apiClient.patch<Cable>(`/api/cables/${id}`, cableData)
  },

  /**
   * Eliminar un cable
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/cables/${id}`)
  },

  /**
   * Obtener cables entre dos nodos
   */
  async getBetweenNodes(origenId: string, destinoId: string): Promise<Cable[]> {
    const allCables = await this.getAll()
    return allCables.filter((cable) => {
      const o = cableOrigenId(cable as any)
      const d = cableDestinoId(cable as any)
      return (
        (o === origenId && d === destinoId) || (o === destinoId && d === origenId)
      )
    })
  },

  /**
   * Obtener cables conectados a un nodo
   */
  async getByNode(nodeId: string): Promise<Cable[]> {
    const allCables = await this.getAll()
    return allCables.filter((cable) => {
      const o = cableOrigenId(cable as any)
      const d = cableDestinoId(cable as any)
      return o === nodeId || d === nodeId
    })
  },
}
